<!-- eslint-disable no-undef -->
<script setup>
  import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
  import Data from '@/api/data';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';
  import {
    normalizeQuickMetricSpecs,
    createEmptyQuickMetric,
    serializeMapAnimationRule,
    validateAnimationRuleOverlaps,
  } from '@/composables/useQuickMetrics';
  import QuickMetricRow from '@/components/common/QuickMetricRow.vue';

  const emit = defineEmits(['submitForm', 'close']);

  const props = defineProps({
    dataID: {
      type: String,
      required: true,
    },
    friendlyName: {
      type: String,
      required: false,
      default: ''
    },
    description: {
      type: String,
      required: false,
      default: ''
    },
    notes: {
      type: String,
      required: false,
      default: ''
    },
    quickMetrics: {
      type: [Array, Object],
      required: false,
      default: () => [],
    },
    snapshots: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    classQuickMetrics: {
      type: Array,
      required: false,
      default: () => [],
    },
    className: {
      type: String,
      required: false,
      default: '',
    },
  });

  // Reactive references
  const friendlyName = ref(props.friendlyName);
  const description = ref(props.description);
  const notes = ref(props.notes);
  const image = ref(null);
  const loading = ref(false);
  const error = ref('');

  const quickMetrics = ref([]);
  const quickMetricSourceOptions = ref({});
  const quickMetricFieldOptions = ref({});
  const quickMetricsLoading = ref(false);
  const quickMetricsError = ref('');
  const hasHydratedQuickMetrics = ref(false);
  const availableSourceNames = computed(() =>
    Object.keys(quickMetricSourceOptions.value).sort((a, b) => a.localeCompare(b))
  );

  const normalizedClassQuickMetrics = computed(() => normalizeQuickMetricSpecs(props.classQuickMetrics));
  const hasClassQuickMetrics = computed(() => normalizedClassQuickMetrics.value.length > 0);

  async function loadQuickMetricSourceOptions() {
    quickMetricsLoading.value = true;
    quickMetricsError.value = '';
    try {
      const response = await Data.get(props.dataID);
      if (response?.body?.status !== 'success') {
        quickMetricsError.value = 'Could not load telemetry fields for this unit.';
        return;
      }

      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages'];
      const sourceOptions = Object.fromEntries(
        Object.entries(response.body.details || {})
          .filter(([key]) => !ignoreSubcollections.includes(key))
          .sort(([a], [b]) => a.localeCompare(b))
      );
      quickMetricSourceOptions.value = sourceOptions;

      const nextFieldOptions = {};
      Object.entries(sourceOptions).forEach(([source, fields]) => {
        nextFieldOptions[source] = Object.entries(fields || {})
          .filter(([, type]) => type !== 'object' && type !== 'array' && type !== 'list' && type !== 'dict')
          .map(([field]) => field)
          .sort((a, b) => a.localeCompare(b));
      });
      quickMetricFieldOptions.value = nextFieldOptions;

      quickMetrics.value = quickMetrics.value.map((metric) => {
        if (!metric.source) return metric;
        if (!nextFieldOptions[metric.source]) {
          return { ...metric, source: '', field: '' };
        }
        if (metric.field && !nextFieldOptions[metric.source].includes(metric.field)) {
          return { ...metric, field: '' };
        }
        return metric;
      });
    } catch (err) {
      console.error('Error loading unit telemetry options:', err);
      quickMetricsError.value = 'Could not load telemetry fields for this unit.';
    } finally {
      quickMetricsLoading.value = false;
    }
  }

  function addQuickMetric() {
    quickMetrics.value = [...quickMetrics.value, createEmptyQuickMetric()];
  }

  function removeQuickMetric(index) {
    quickMetrics.value = quickMetrics.value.filter((_, i) => i !== index);
  }

  function copyFromClassMetrics() {
    if (!hasClassQuickMetrics.value) return;
    quickMetrics.value = normalizedClassQuickMetrics.value.map((metric) => ({ ...metric }));
    error.value = '';
  }

  function hydrateFormFromProps() {
    friendlyName.value = props.friendlyName;
    description.value = props.description;
    notes.value = props.notes;
    const explicitMetrics = normalizeQuickMetricSpecs(props.quickMetrics);
    quickMetrics.value = explicitMetrics.length > 0
      ? explicitMetrics
      : normalizeQuickMetricSpecs(props.snapshots);
    hasHydratedQuickMetrics.value = true;
    error.value = '';
  }

  watch(
    () => props.dataID,
    () => {
      // Rehydrate only when opening/switching to a different unit.
      // Avoid wiping in-progress edits from live parent updates.
      hasHydratedQuickMetrics.value = false;
      hydrateFormFromProps();
    },
    { immediate: true }
  );

  watch(
    () => props.quickMetrics,
    (nextQuickMetrics) => {
      const normalized = normalizeQuickMetricSpecs(nextQuickMetrics);
      if (normalized.length === 0) return;
      // Backfill quick metrics if they arrive shortly after modal opens.
      // This avoids empty form rows when parent device details stream in late.
      if (!hasHydratedQuickMetrics.value || quickMetrics.value.length === 0) {
        quickMetrics.value = normalized;
        hasHydratedQuickMetrics.value = true;
      }
    },
    { deep: true }
  );

  watch(
    () => props.snapshots,
    (nextSnapshots) => {
      if (quickMetrics.value.length > 0) return;
      const derived = normalizeQuickMetricSpecs(nextSnapshots);
      if (derived.length === 0) return;
      quickMetrics.value = derived;
      hasHydratedQuickMetrics.value = true;
    },
    { deep: true }
  );

  // Lifecycle hooks
  onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
    loadQuickMetricSourceOptions();
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
  });

  // Methods
  const handleSubmit = () => {
    if (loading.value) return;
    error.value = '';

    const hasPartiallyFilledMetric = quickMetrics.value.some((metric) => {
      const hasAnyValue = !!(
        (metric.source || '').trim() ||
        (metric.field || '').trim() ||
        (metric.friendlyName || '').trim() ||
        (metric.unit || '').trim()
      );
      if (!hasAnyValue) return false;
      return !((metric.source || '').trim() && (metric.field || '').trim());
    });

    if (hasPartiallyFilledMetric) {
      error.value = 'Each quick metric must include both a source and field.';
      return;
    }

    loading.value = true;
    const serializedQuickMetrics = [];
    for (const metric of normalizeQuickMetricSpecs(quickMetrics.value)) {
      const serializedRule = serializeMapAnimationRule(metric.mapAnimationRule);
      if (serializedRule?.error) {
        error.value = `Quick metric "${metric.friendlyName || metric.field}": ${serializedRule.error}`;
        return;
      }
      serializedQuickMetrics.push({
        source: metric.source,
        field: metric.field,
        friendlyName: metric.friendlyName,
        unit: metric.unit,
        ...(serializedRule ? { mapAnimationRule: serializedRule } : {}),
      });
    }
    const overlapError = validateAnimationRuleOverlaps(serializedQuickMetrics);
    if (overlapError) {
      error.value = overlapError;
      return;
    }

    const formData = {
      friendlyName: friendlyName.value,
      description: description.value,
      notes: notes.value,
      image: image.value,
      quickMetrics: serializedQuickMetrics,
    };
    emit('submitForm', formData);
  };

  const setFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      image.value = file;
    }
  };

  const close = () => {
    loading.value = false;
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);

  // Handle ESC key
  const handleKeydown = (event) => {
    if (event.key === 'Escape') {
      close();
    }
  };
</script>

<template>
    <div
      class="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
      @mousedown.self="onBackdropMousedown"
      @mouseup.self="onBackdropMouseup"
      role="dialog"
      aria-labelledby="modal-title"
    >
      <div
        class="ui-modal bg-mc-white dark:bg-mc-charcoal rounded-xl w-[95vw] max-w-[860px] max-h-[calc(100vh-50px)] flex flex-col overflow-hidden"
        @mousedown="onContentMousedown"
      >

        <!-- Header -->
        <div class="flex flex-col px-6 pt-4 pb-0 border-b border-mc-steel/50 dark:border-line-on-dark shrink-0">
          <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
          <div class="flex items-center justify-between pb-4">
            <h2 id="modal-title" class="font-extrabold text-xl text-mc-ink dark:text-on-dark tracking-tight">Edit Unit</h2>
            <button
              type="button"
              @click="close"
              class="p-1.5 rounded-mc-sm text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/30 dark:hover:bg-white/10 hover:text-mc-ink dark:hover:text-on-dark transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              aria-label="Close"
            >
              <svg class="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round">
                <path d="M2 2l12 12M14 2L2 14"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Scrollable body -->
        <div class="overflow-y-auto px-6 py-5 flex-1 min-h-0">
          <form @submit.prevent="handleSubmit" class="flex flex-col gap-5">

            <!-- Basic fields: name + description/notes side-by-side -->
            <div>
              <label class="block font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1.5">Friendly Name</label>
              <input v-model="friendlyName" type="text" class="input-base dark:bg-mc-charcoal dark:text-on-dark" placeholder="e.g. Rooftop Sensor A" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="block font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1.5">Description</label>
                <textarea v-model="description" rows="4" class="input-base resize-none dark:bg-mc-charcoal dark:text-on-dark" placeholder="Short description of this unit…"></textarea>
              </div>
              <div>
                <label class="block font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1.5">Notes</label>
                <textarea v-model="notes" rows="4" class="input-base resize-none dark:bg-mc-charcoal dark:text-on-dark" placeholder="Internal notes…"></textarea>
              </div>
            </div>

            <!-- Image upload -->
            <div>
              <label class="block font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1.5">Image</label>
              <label class="flex items-center gap-3 px-3 py-2.5 rounded-mc-md border border-dashed border-mc-steel dark:border-line-on-dark hover:border-mc-ink dark:hover:border-on-dark-muted cursor-pointer transition-colors duration-160 group">
                <svg class="w-4 h-4 text-ink-muted dark:text-on-dark-muted shrink-0 group-hover:text-mc-ink dark:group-hover:text-on-dark transition-colors duration-160" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M8 10V3M5 6l3-3 3 3M3 11v1.5A1.5 1.5 0 004.5 14h7A1.5 1.5 0 0013 12.5V11"/>
                </svg>
                <span class="font-body text-sm text-ink-muted dark:text-on-dark-muted group-hover:text-mc-ink dark:group-hover:text-on-dark transition-colors duration-160">
                  {{ image ? image.name : 'Choose image…' }}
                </span>
                <input type="file" accept="image/*" @change="setFile($event)" class="sr-only" />
              </label>
            </div>

            <!-- Quick Metrics -->
            <div class="rounded-mc-md border border-mc-steel dark:border-line-on-dark overflow-hidden">

              <!-- Section header -->
              <div class="flex items-center justify-between gap-2 px-4 py-3 bg-mc-cloud dark:bg-black/30 border-b border-mc-steel dark:border-line-on-dark flex-wrap gap-y-2">
                <div>
                  <span class="font-mono text-xs uppercase tracking-[0.08em] text-mc-ink dark:text-on-dark font-semibold">Quick Metrics</span>
                  <span class="font-body text-xs text-ink-muted dark:text-on-dark-muted ml-2">shown on dashboard cards</span>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="hasClassQuickMetrics"
                    type="button"
                    class="btn-secondary text-xs py-1 px-2.5"
                    @click="copyFromClassMetrics"
                  >
                    Copy from {{ className || 'class' }}
                  </button>
                  <button
                    type="button"
                    class="btn-secondary text-xs py-1 px-2.5"
                    @click="addQuickMetric"
                    :disabled="quickMetricsLoading || availableSourceNames.length === 0"
                  >
                    + Add Metric
                  </button>
                </div>
              </div>

              <!-- Metrics body -->
              <div class="p-3 space-y-2">
                <p v-if="quickMetricsLoading" class="font-body text-xs text-ink-muted dark:text-on-dark-muted py-2">Loading telemetry fields…</p>
                <p v-else-if="quickMetricsError" class="font-body text-xs text-mc-red py-2">{{ quickMetricsError }}</p>

                <template v-if="quickMetrics.length > 0">
                  <QuickMetricRow
                    v-for="(metric, index) in quickMetrics"
                    :key="`unit-quick-metric-${index}`"
                    :metric="metric"
                    :source-names="availableSourceNames"
                    :field-options="quickMetricFieldOptions"
                    @update:metric="quickMetrics[index] = $event"
                    @remove="removeQuickMetric(index)"
                  />
                </template>

                <p v-else class="font-body text-xs text-ink-muted dark:text-on-dark-muted py-2">
                  No quick metrics configured. Dashboard will auto-detect metrics for this unit.
                </p>
              </div>
            </div>

          </form>
        </div>

        <!-- Sticky footer -->
        <div class="flex items-center justify-between px-6 py-4 border-t border-mc-steel/50 dark:border-line-on-dark shrink-0 bg-mc-white dark:bg-mc-charcoal">
          <p v-if="error" class="font-body text-sm text-mc-red">{{ error }}</p>
          <span v-else />
          <div class="flex items-center gap-3">
            <LoadingAnimation3 v-if="loading" size="22px" />
            <button type="button" @click="close" class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160" :disabled="loading">Cancel</button>
            <button type="submit" form="update-device-form" :disabled="loading" class="btn-primary" @click.prevent="handleSubmit">
              {{ loading ? 'Saving…' : 'Save Changes' }}
            </button>
          </div>
        </div>

      </div>
    </div>
</template>
